import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, CircularProgress,
  Alert, Stack, Paper, Chip, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, ListSubheader, IconButton, Tooltip
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ShortTextIcon from '@mui/icons-material/ShortText';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import SpeedIcon from '@mui/icons-material/Speed';
import { rewriteAndSave } from '../api';

/* ── Mode definitions with descriptions ── */
const REWRITE_MODES = [
  {
    group: 'General',
    color: '#8B5CF6',
    items: [
      {
        id: 'magic',
        label: 'Magic',
        icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />,
        description: 'Professional & polished',
        color: '#8B5CF6',
        pro: false,
      },
      {
        id: 'slightly',
        label: 'Light Touch',
        icon: <HistoryEduIcon sx={{ fontSize: 18 }} />,
        description: 'Minimal edits only',
        color: '#A78BFA',
        pro: true,
      },
      {
        id: 'significantly',
        label: 'Full Rewrite',
        icon: <HistoryEduIcon sx={{ fontSize: 18 }} />,
        description: 'Sophisticated & polished',
        color: '#7C3AED',
        pro: true,
      },
    ],
  },
  {
    group: 'Text Editing',
    color: '#06B6D4',
    items: [
      {
        id: 'structured',
        label: 'Structured Report',
        icon: <AlignHorizontalLeftIcon sx={{ fontSize: 18 }} />,
        description: 'Sections & headings',
        color: '#06B6D4',
        pro: true,
      },
      {
        id: 'list',
        label: 'Bullet Points',
        icon: <FormatListBulletedIcon sx={{ fontSize: 18 }} />,
        description: 'Clean key points list',
        color: '#22D3EE',
        pro: true,
      },
    ],
  },
  {
    group: 'Summary',
    color: '#10B981',
    items: [
      {
        id: 'detailed',
        label: 'Detailed Summary',
        icon: <SummarizeIcon sx={{ fontSize: 18 }} />,
        description: 'Comprehensive coverage',
        color: '#10B981',
        pro: true,
      },
      {
        id: 'short',
        label: 'Quick Summary',
        icon: <ShortTextIcon sx={{ fontSize: 18 }} />,
        description: '2–3 sentence overview',
        color: '#34D399',
        pro: true,
      },
      {
        id: 'takeaways',
        label: 'Action Items',
        icon: <AssignmentTurnedInIcon sx={{ fontSize: 18 }} />,
        description: 'Decisions & next steps',
        color: '#059669',
        pro: true,
      },
    ],
  },
];

/* ── Orbiting dots loader ── */
function OrbitLoader() {
  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <div className="orbit-container">
        <div className="orbit-dot orbit-dot-1" />
        <div className="orbit-dot orbit-dot-2" />
        <div className="orbit-dot orbit-dot-3" />
      </div>
      <Typography variant="h6" sx={{ mt: 3, mb: 1, fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700 }} className="logo-shimmer">
        AI is working its magic…
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569' }}>
        Analyzing and transforming your meeting notes.
      </Typography>
    </Box>
  );
}

/* ── Stats strip ── */
function StatsStrip({ text }) {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const chars = text.length;
  const readingMinutes = Math.max(1, Math.round(words / 200));
  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
      {[
        { icon: <TextFieldsIcon sx={{ fontSize: 13 }} />, label: `${words} words`, color: '#8B5CF6' },
        { icon: <SpeedIcon sx={{ fontSize: 13 }} />, label: `~${readingMinutes} min read`, color: '#06B6D4' },
        { icon: <TextFieldsIcon sx={{ fontSize: 13 }} />, label: `${chars} chars`, color: '#10B981' },
      ].map(({ icon, label, color }) => (
        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: '8px', background: `${color}14`, border: `1px solid ${color}28` }}>
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}>{label}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

export default function RewritePanel({ meeting, onSummaryDone, showSnackbar }) {
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [activeMode, setActiveMode]     = useState('magic');
  const [generatedAt, setGeneratedAt]   = useState(null);
  const [copied, setCopied]             = useState(false);

  const summary = meeting?.summary || {};
  const hasContent = !!summary.overall_summary || (summary.key_points && summary.key_points.length > 0);

  /* ── Auto-detect saved summary mode when meeting loads ── */
  useEffect(() => {
    if (summary.overall_summary && !loading) {
      // If meeting already has a saved summary, show it immediately
      setGeneratedAt(null);
    }
  }, [meeting?.meeting_id]);

  /* ── Get the current mode's config ── */
  const activeModeConfig = useMemo(() => {
    for (const group of REWRITE_MODES) {
      const found = group.items.find(i => i.id === activeMode);
      if (found) return found;
    }
    return REWRITE_MODES[0].items[0];
  }, [activeMode]);

  /* ── Build full output text for copy/download ── */
  const outputText = useMemo(() => {
    if (!hasContent) return '';
    const lines = [];
    if (['list', 'takeaways', 'structured'].includes(activeMode) && summary.key_points?.length > 0) {
      lines.push(...summary.key_points.map((p, i) => `${i + 1}. ${p}`));
    }
    if (!['list', 'takeaways'].includes(activeMode) && summary.overall_summary) {
      lines.push(summary.overall_summary);
    }
    return lines.join('\n');
  }, [hasContent, activeMode, summary]);

  /* ── Core rewrite handler ── */
  const handleRewrite = async (modeId) => {
    if (!meeting?.full_text) return;
    setActiveMode(modeId);
    setError('');
    setLoading(true);
    try {
      const result = await rewriteAndSave(meeting.meeting_id, meeting.full_text, modeId);
      onSummaryDone(result);
      setGeneratedAt(new Date());
      showSnackbar(`✨ ${modeId.charAt(0).toUpperCase() + modeId.slice(1)} rewrite applied!`, 'success');
    } catch (err) {
      setError(err.message || 'Rewrite failed');
    } finally {
      setLoading(false);
    }
  };

  /* ── Regenerate same mode ── */
  const handleRegenerate = () => handleRewrite(activeMode);

  /* ── Copy to clipboard ── */
  const handleCopy = () => {
    if (!outputText) return;
    navigator.clipboard.writeText(outputText).then(() => {
      setCopied(true);
      showSnackbar('Copied to clipboard!', 'success');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  /* ── Download as .txt ── */
  const handleDownload = () => {
    if (!outputText) return;
    const modeLabel = activeModeConfig?.label || activeMode;
    const filename = `${meeting?.title || 'meeting'}_${modeLabel.replace(/\s+/g, '_')}.txt`.toLowerCase();
    const blob = new Blob([outputText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    showSnackbar('Downloaded!', 'success');
  };

  return (
    <Box className="fade-in-up" sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>

      {/* ══ LEFT: Mode selector sidebar ══ */}
      <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
        <Paper className="glass-card" sx={{ borderRadius: '20px', overflow: 'hidden' }}>

          {/* Header */}
          <Box sx={{ p: 2.5, background: 'rgba(139,92,246,0.06)', borderBottom: '1px solid rgba(139,92,246,0.1)' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AutoAwesomeIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
              <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Rewrite Mode
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.5 }}>
              Select how AI should transform your notes
            </Typography>
          </Box>

          {/* Mode list */}
          <List sx={{ py: 1.5, px: 0.5 }} disablePadding>
            {REWRITE_MODES.map((group) => (
              <React.Fragment key={group.group}>
                <ListSubheader sx={{
                  background: 'transparent',
                  lineHeight: '28px',
                  fontWeight: 800,
                  fontSize: 10,
                  color: group.color,
                  textTransform: 'uppercase',
                  letterSpacing: 1.2,
                  mt: 1.5,
                  fontFamily: '"JetBrains Mono", monospace',
                }}>
                  {group.group}
                </ListSubheader>
                {group.items.map((item) => {
                  const isActive = activeMode === item.id;
                  return (
                    <ListItem disablePadding key={item.id} sx={{ mb: 0.3 }}>
                      <Tooltip title={item.description} placement="right" arrow>
                        <ListItemButton
                          onClick={() => handleRewrite(item.id)}
                          disabled={loading}
                          selected={isActive}
                          sx={{
                            mx: 0.5,
                            borderRadius: '12px',
                            py: 1.2,
                            transition: 'all 0.2s ease',
                            border: '1px solid transparent',
                            '&.Mui-selected': {
                              background: `${item.color}14`,
                              borderColor: `${item.color}30`,
                              '&:hover': { background: `${item.color}20` },
                            },
                            '&:hover': {
                              background: 'rgba(139,92,246,0.06)',
                              transform: 'translateX(3px)',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: isActive ? item.color : '#475569' }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            secondary={item.description}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 800 : 600, color: isActive ? item.color : '#94A3B8', fontSize: 13 }}
                            secondaryTypographyProps={{ fontSize: 11, color: '#374151' }}
                          />
                          {isActive && loading && (
                            <CircularProgress size={14} sx={{ color: item.color, ml: 1 }} />
                          )}
                          {item.pro && !isActive && (
                            <Chip label="PRO" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 900, background: 'rgba(6,182,212,0.12)', color: '#06B6D4', borderRadius: '6px', ml: 0.5 }} />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  );
                })}
              </React.Fragment>
            ))}
          </List>

          <Divider sx={{ mx: 2, opacity: 0.5 }} />

          {/* Footer info */}
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#1E293B', fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }}>
              POWERED BY BART AI
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* ══ RIGHT: Result panel ══ */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Card className="glass-card" sx={{ minHeight: 480, borderRadius: '20px' }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>

            {/* ── Empty state ── */}
            {!hasContent && !loading && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 38, color: '#8B5CF6' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} mb={1} sx={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                  Generate Your Report
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', maxWidth: 360, mx: 'auto', lineHeight: 1.65, mb: 1 }}>
                  Click any mode on the left to transform your meeting notes. Your AI-generated report will appear right here.
                </Typography>
                {!meeting?.full_text && (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: '12px', maxWidth: 400, mx: 'auto' }}>
                    This meeting has no transcript text. Record or upload audio first.
                  </Alert>
                )}
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
                  {[
                    { label: '✨ Magic', desc: 'Click to try' },
                    { label: '📋 Bullets', desc: 'Click to try' },
                    { label: '📝 Summary', desc: 'Click to try' },
                  ].map(({ label }) => (
                    <Chip key={label} label={label} size="small" sx={{ background: 'rgba(139,92,246,0.08)', color: '#94A3B8', border: '1px solid rgba(139,92,246,0.15)', fontSize: 11, fontWeight: 600 }} />
                  ))}
                </Stack>
              </Box>
            )}

            {/* ── Loading state ── */}
            {loading && <OrbitLoader />}

            {/* ── Result ── */}
            {!loading && hasContent && (
              <Box className="fade-in-up">

                {/* Result header */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={1}>
                    {/* Mode badge */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.8,
                      px: 1.8, py: 0.6, borderRadius: '10px',
                      background: `${activeModeConfig?.color || '#8B5CF6'}18`,
                      border: `1px solid ${activeModeConfig?.color || '#8B5CF6'}30`,
                    }}>
                      <Box sx={{ color: activeModeConfig?.color || '#8B5CF6', display: 'flex', fontSize: 14 }}>
                        {activeModeConfig?.icon}
                      </Box>
                      <Typography variant="caption" sx={{ color: activeModeConfig?.color || '#8B5CF6', fontWeight: 800, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: '"JetBrains Mono", monospace' }}>
                        {activeModeConfig?.label || activeMode}
                      </Typography>
                    </Box>

                    {/* Timestamp */}
                    {generatedAt && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AccessTimeIcon sx={{ fontSize: 12, color: '#374151' }} />
                        <Typography variant="caption" sx={{ color: '#374151', fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}>
                          {generatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {/* Action buttons */}
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Regenerate" arrow>
                      <IconButton
                        onClick={handleRegenerate}
                        disabled={loading}
                        size="small"
                        sx={{ border: '1px solid rgba(139,92,246,0.2)', borderRadius: '10px', color: '#8B5CF6', '&:hover': { background: 'rgba(139,92,246,0.1)', borderColor: '#8B5CF6' } }}
                      >
                        <RefreshIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'} arrow>
                      <IconButton
                        onClick={handleCopy}
                        disabled={!outputText}
                        size="small"
                        sx={{ border: `1px solid ${copied ? 'rgba(16,185,129,0.4)' : 'rgba(6,182,212,0.2)'}`, borderRadius: '10px', color: copied ? '#10B981' : '#06B6D4', '&:hover': { background: 'rgba(6,182,212,0.1)' } }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download as .txt" arrow>
                      <IconButton
                        onClick={handleDownload}
                        disabled={!outputText}
                        size="small"
                        sx={{ border: '1px solid rgba(139,92,246,0.2)', borderRadius: '10px', color: '#A78BFA', '&:hover': { background: 'rgba(139,92,246,0.1)' } }}
                      >
                        <DownloadIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {/* Stats strip */}
                {outputText && <StatsStrip text={outputText} />}

                {/* ── Bullet list modes ── */}
                {['list', 'takeaways', 'structured'].includes(activeMode) && summary.key_points && summary.key_points.length > 0 && (
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
                      <FormatListBulletedIcon sx={{ color: '#06B6D4', fontSize: 18 }} />
                      <Typography variant="h6" fontWeight={800} sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: 16 }}>
                        {activeMode === 'takeaways' ? 'Action Items & Decisions' : activeMode === 'list' ? 'Key Points' : 'Structured Report'}
                      </Typography>
                    </Stack>
                    <Stack spacing={1.5}>
                      {summary.key_points.map((point, i) => (
                        <Paper key={i} elevation={0} sx={{
                          p: 2, borderRadius: '14px',
                          background: 'rgba(6,182,212,0.04)',
                          border: '1px solid rgba(6,182,212,0.1)',
                          borderLeft: '3px solid #06B6D4',
                          display: 'flex', alignItems: 'flex-start', gap: 1.5,
                          transition: 'all 0.2s ease',
                          '&:hover': { background: 'rgba(6,182,212,0.08)', transform: 'translateX(3px)' },
                        }}>
                          <Box sx={{
                            width: 22, height: 22, borderRadius: '6px', flexShrink: 0,
                            background: 'rgba(6,182,212,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            mt: 0.2,
                          }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#22D3EE', fontFamily: '"JetBrains Mono", monospace' }}>
                              {String(i + 1).padStart(2, '0')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#E2E8F0', lineHeight: 1.7, flex: 1 }}>
                            {point}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* ── Narrative text modes ── */}
                {!['list', 'takeaways'].includes(activeMode) && summary.overall_summary && (
                  <Paper elevation={0} sx={{
                    p: 3, borderRadius: '16px',
                    background: 'rgba(139,92,246,0.04)',
                    border: '1px solid rgba(139,92,246,0.1)',
                    borderLeft: '3px solid #8B5CF6',
                  }}>
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.85,
                        fontSize: '1.03rem',
                        color: '#E2E8F0',
                        whiteSpace: 'pre-wrap',
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      {summary.overall_summary}
                    </Typography>
                  </Paper>
                )}

              </Box>
            )}

            {/* Error */}
            {error && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: '12px' }}>
                {error}
                {error.includes('warming up') && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRegenerate}
                    sx={{ ml: 2, borderRadius: '8px', fontSize: 11 }}
                  >
                    Retry
                  </Button>
                )}
              </Alert>
            )}

          </CardContent>
        </Card>
      </Box>

    </Box>
  );
}
